import { useCallback, useEffect, useRef } from 'react';
import { useFetcher } from 'react-router';

import { AnalyticsEvents } from '~/constants/analytics';
import { useVendorsListUrlParams } from '~/hooks/url-params/useVendorsListUrlParams';
import { useAnalyticsContext } from '~/provider/AnalyticsProvider';
import { useLeadContext } from '~/provider/LeadProvider';
import { CustomerFormValues, MarketplaceLead } from '~/types/marketplace';

type SelectedVendorFetcherData = {
  leadId?: string;
  error?: Record<string, string> | string;
};

type GetLeadFetcherData = {
  lead: MarketplaceLead | null;
};

type UseLeadCreationFetcherOptions = {
  onRequestQuotes: () => void;
  close: () => void;
  closeLanding: () => void;
};

type UseLeadCreationFetcherResult = {
  onSubmit: (data: Partial<CustomerFormValues>) => void;
  isLoading: boolean;
  error: Record<string, string> | string | undefined;
  lead: MarketplaceLead | null;
  postcode: string;
  selectedCategories: string[] | undefined;
  track: (event: AnalyticsEvents) => void;
};

export const useLeadCreationFetcher = ({
  onRequestQuotes,
  close,
  closeLanding,
}: UseLeadCreationFetcherOptions): UseLeadCreationFetcherResult => {
  const fetcher = useFetcher<SelectedVendorFetcherData>({ key: 'lead-submit' });
  const leadFetcher = useFetcher<GetLeadFetcherData>({ key: 'lead-fetch' });
  const processedLeadId = useRef<string | null>(null);
  const hasCalledOnRequestQuotes = useRef(false);

  const { lead, setLead } = useLeadContext();
  const { postcode, categories: selectedCategories, setQueryParams } = useVendorsListUrlParams({ lead });
  const { track: trackEvent } = useAnalyticsContext();
  const track = useCallback(
    (event: AnalyticsEvents): void => {
      trackEvent(event);
    },
    [trackEvent],
  );

  useEffect(() => {
    // append leadId to url params and fetch full lead data
    const leadId = fetcher.data?.leadId;
    if (leadId && processedLeadId.current !== leadId) {
      processedLeadId.current = leadId;
      setQueryParams(
        (current) => ({
          ...current,
          leadId: leadId ?? current.leadId,
        }),
        {
          history: 'push',
        },
      );

      leadFetcher.load(`/api/lead/get-lead?leadId=${encodeURIComponent(leadId)}`);
    }
  }, [fetcher.data]);

  useEffect(() => {
    // set lead data in context once fetched, then open quote request modal.
    // also handle 401/failed fetch (lead is null) by proceeding anyway — session may
    // not be established yet, but the leadId is already in the URL params.
    // hasCalledOnRequestQuotes ensures this only runs once — React Router revalidation can give
    // leadFetcher.data a new object reference, which would re-trigger this effect without it.
    if (
      leadFetcher.data !== undefined &&
      'lead' in leadFetcher.data &&
      processedLeadId.current &&
      !hasCalledOnRequestQuotes.current
    ) {
      hasCalledOnRequestQuotes.current = true;
      if (leadFetcher.data.lead) {
        setLead(leadFetcher.data.lead);
      }
      trackEvent(AnalyticsEvents.SelectedRequestQuotesClicked);
      onRequestQuotes();
      close();
      closeLanding();
    }
  }, [leadFetcher.data]);

  const onSubmit = (data: Partial<CustomerFormValues>) => {
    fetcher.submit(data, { method: 'post' });
  };

  return {
    onSubmit,
    isLoading: fetcher.state !== 'idle' || leadFetcher.state !== 'idle',
    error: fetcher.data?.error,
    lead,
    postcode,
    selectedCategories,
    track,
  };
};
